import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import 'bootstrap/dist/css/bootstrap.min.css';

const ResourceDistribution = ({ summary }) => {
  const categoryMapping = {
    // Compute
    'ec2': 'Compute', 'ec2-instances': 'Compute', 'instances': 'Compute',
    'ami': 'Compute', 'amis': 'Compute',
    'asg': 'Compute', 'launch-config': 'Compute', 'launch-configs': 'Compute',
    'batch-compute': 'Compute', 'batch-definition': 'Compute', 'batch-queue': 'Compute',
    
    // Serverless
    'lambda': 'Serverless', 'lambda-functions': 'Serverless', 'functions': 'Serverless',
    'lambda-layer': 'Serverless', 'lambda-layers': 'Serverless',
    'step-machine': 'Serverless',
    
    // Storage
    's3': 'Storage', 's3-buckets': 'Storage', 'buckets': 'Storage',
    'ebs': 'Storage', 'volumes': 'Storage', 'ebs-snapshot': 'Storage', 'ebs-snapshots': 'Storage',
    'efs': 'Storage', 'fsx': 'Storage', 'glacier': 'Storage', 'glacier-vaults': 'Storage',
    
    // Database
    'dynamodb-table': 'Database', 'dynamodb-tables': 'Database',
    'rds': 'Database', 'rds-instances': 'Database', 'databases': 'Database',
    'rds-cluster': 'Database', 'rds-clusters': 'Database',
    'rds-snapshot': 'Database', 'rds-snapshots': 'Database',
    'redshift': 'Database', 'redshift-clusters': 'Database',
    'elasticache-group': 'Database', 'cache-cluster': 'Database',
    
    // Network
    'vpc': 'Network', 'vpcs': 'Network',
    'subnet': 'Network', 'subnets': 'Network',
    'security-group': 'Network', 'security-groups': 'Network',
    'internet-gateway': 'Network', 'internet-gateways': 'Network',
    'nat-gateway': 'Network', 'nat-gateways': 'Network',
    'route-table': 'Network', 'route-tables': 'Network',
    'network-acl': 'Network', 'network-acls': 'Network',
    'eni': 'Network', 'enis': 'Network',
    'elastic-ip': 'Network', 'elastic-ips': 'Network',
    'transit-gateway': 'Network', 'transit-gateways': 'Network',
    'vpn-gateway': 'Network', 'vpn-gateways': 'Network',
    'vpn-connection': 'Network', 'vpn-connections': 'Network',
    'elb': 'Network', 'elbs': 'Network',
    'app-elb': 'Network', 'app-elbs': 'Network', 'load_balancers': 'Network',
    'app-elb-target-group': 'Network', 'app-elb-target-groups': 'Network',
    'distribution': 'Network', 'distributions': 'Network', 'cloudfront-distributions': 'Network',
    'rest-api': 'Network', 'rest-apis': 'Network', 'api-gateways': 'Network',
    'apigwv2': 'Network',
    
    // Security
    'iam-user': 'Security', 'iam-users': 'Security',
    'iam-role': 'Security', 'iam-roles': 'Security',
    'iam-group': 'Security', 'iam-groups': 'Security',
    'iam-policy': 'Security', 'iam-policies': 'Security',
    'acm-certificate': 'Security',
    'secrets-manager': 'Security', 'secrets-manager-secrets': 'Security',
    'kms': 'Security', 'kms-key': 'Security',
    'waf': 'Security', 'wafv2': 'Security',
    'guardduty-finding': 'Security', 'guardduty-findings': 'Security',
    
    // Containers
    'ecs': 'Containers', 'ecs-clusters': 'Containers', 'clusters': 'Containers',
    'ecs-service': 'Containers', 'ecs-services': 'Containers',
    'ecs-task': 'Containers', 'ecs-tasks': 'Containers',
    'eks': 'Containers', 'eks-clusters': 'Containers',
    'ecr': 'Containers', 'ecr-repositories': 'Containers',
    
    // Monitoring
    'alarm': 'Monitoring', 'alarms': 'Monitoring', 'cloudwatch-alarms': 'Monitoring',
    'log-group': 'Monitoring', 'log-groups': 'Monitoring',
    'cloudtrail': 'Monitoring', 'cloudtrail-trails': 'Monitoring',
    'config-rule': 'Monitoring', 'config-rules': 'Monitoring',
    'event-rule': 'Monitoring', 'event-rules': 'Monitoring',
    
    // Analytics
    'kinesis': 'Analytics', 'kinesis-streams': 'Analytics',
    'firehose': 'Analytics', 'firehose-deliveries': 'Analytics',
    'emr': 'Analytics',
    'glue-job': 'Analytics', 'glue-crawler': 'Analytics',
    'athena-work-group': 'Analytics', 'athena-work-groups': 'Analytics',
    
    // Application Integration
    'sqs': 'Integration', 'sqs-queues': 'Integration',
    'sns': 'Integration', 'sns-topics': 'Integration',
    
    // Machine Learning
    'sagemaker-notebook': 'ML', 'sagemaker-notebooks': 'ML',
    'sagemaker-endpoint': 'ML', 'sagemaker-endpoints': 'ML',
    'sagemaker-model': 'ML', 'sagemaker-models': 'ML',
    
    // DevOps
    'codebuild': 'DevOps', 'codebuild-projects': 'DevOps',
    'codecommit': 'DevOps', 'codecommit-repositories': 'DevOps',
    'codepipeline': 'DevOps', 'codepipeline-pipelines': 'DevOps',
    'cfn': 'DevOps', 'cloudformation': 'DevOps', 'cloudformation-stacks': 'DevOps',
    
    // Management
    'ssm-parameter': 'Management', 'ssm-parameters': 'Management', 'systems-manager-parameters': 'Management',
    'backup-plan': 'Management', 'backup-vault': 'Management',
  };

  const [chartData, setChartData] = useState(null);

  const defaultOptions = {
    chart: {
      type: 'donut',
      height: 'auto',
      dropShadow: {
        enabled: true,
        top: 5,
        left: 5,
        blur: 10,
        opacity: 0.3,
      },
      toolbar: { show: false },
    },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: '70%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total Resources',
              fontSize: '16px',
              fontWeight: 600,
              color: '#333',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              },
            },
          },
        },
        expandOnClick: true,
      },
    },
    colors: [
      'rgba(52, 46, 173, 0.9)',
      'rgba(86, 154, 49, 0.9)',
      'rgba(255, 153, 0, 0.9)',
      'rgba(82, 127, 255, 0.9)',
      'rgba(140, 79, 255, 0.9)',
      'rgba(239, 68, 68, 0.9)',
      'rgba(245, 158, 11, 0.9)',
      'rgba(16, 185, 129, 0.9)',
      'rgba(236, 72, 153, 0.9)',
    ],
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'dark',
        type: 'vertical',
        shadeIntensity: 0.5,
        gradientToColors: ['#527FFF', '#81C784', '#FFC107', '#64B5F6', '#AB47BC', '#FF616D', '#FFB74D', '#4DB6AC', '#F06292'],
        opacityFrom: 0.9,
        opacityTo: 1,
        stops: [0, 100],
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['#fff'],
    },
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      fontSize: '12px',
      labels: {
        colors: '#333',
        useSeriesColors: false,
      },
      markers: { radius: 5 },
      itemMargin: {
        horizontal: 6,
        vertical: 6,
      },
      offsetY: 0,
      height: 80,
    },
    tooltip: {
      y: {
        formatter: function (value) {
          return `${value} resources`;
        },
      },
    },
  };

  useEffect(() => {
    if (!summary?.resourceTypes || !Array.isArray(summary.resourceTypes) || summary.resourceTypes.length === 0) {
      return;
    }

    const categories = {};
    
    summary.resourceTypes.forEach(rt => {
      if (rt && rt.type && typeof rt.count === 'number' && rt.count > 0) {
        const category = categoryMapping[rt.type] || 'Other';
        categories[category] = (categories[category] || 0) + rt.count;
      }
    });

    const labels = Object.keys(categories);
    const series = Object.values(categories);

    if (labels.length > 0 && series.length > 0) {
      setChartData({
        series,
        options: {
          ...defaultOptions,
          labels,
        },
      });
    }
  }, [summary]);



  return (
    <div
      className="rounded shadow p-4"
      style={{
        background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)',
        border: '1px solid #ddd',
        borderRadius: '10px',
        height: '480px',
      }}
    >
      <h5 className="fw-bold text-muted mb-3" style={{ color: '#333' }}>
        Resource Distribution by Category
      </h5>
      <div style={{ height: '400px' }}>
        {chartData ? (
          <Chart
            options={chartData.options}
            series={chartData.series}
            type="donut"
            height={400}
          />
        ) : (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
            <p className="text-muted">No data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourceDistribution;
